import { getRoleEntityTags } from '@onefootprint/core';
import { patchOrgRolesByTenantRoleIdMutation } from '@onefootprint/fetch-dashboard';
import { getErrorMessage } from '@onefootprint/request/src/request';
import type { OrganizationRole, UpdateTenantRoleRequest } from '@onefootprint/request-types/dashboard';
import { Dialog, useToast } from '@onefootprint/ui';
import { useMutation } from '@tanstack/react-query';
import { forwardRef, useImperativeHandle, useState } from 'react';
import { useTranslation } from 'react-i18next';

import Form from '@/components/roles/create-dialog/components/role-form';
import { getDecryptAccess } from '@/components/roles/utils/decrypt-access';
import { groupTenantRoleScopes } from '@/components/roles/utils/group-scopes';
import { getAccess } from '@/components/roles/utils/scope-environments';
import useInvalidateQueries from '@/hooks/use-invalidate-queries';

type EditHandler = {
  edit: () => void;
};

type EditDialogProps = {
  role: OrganizationRole;
};

const EditDialog = forwardRef<EditHandler, EditDialogProps>(({ role }, ref) => {
  const { t: allT } = useTranslation('common');
  const { t } = useTranslation('roles', { keyPrefix: 'edit' });
  const { toast } = useToast();
  const invalidateQueries = useInvalidateQueries();
  const [open, setOpen] = useState(false);
  const editRoleMutation = useMutation(patchOrgRolesByTenantRoleIdMutation({ throwOnError: true }));
  const { vaultProxyOptions } = groupTenantRoleScopes(role.scopes);
  const formKind = role.kind;

  const handleOpen = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  const handleSubmit = (payload: UpdateTenantRoleRequest) => {
    editRoleMutation.mutate(
      {
        body: payload,
        path: { tenantRoleId: role.id },
      },
      {
        onSuccess: response => {
          toast({
            title: t($ => $.notifications.success.title),
            description: t($ => $.notifications.success.description, {
              name: response?.name,
            }),
          });
          invalidateQueries();
          handleClose();
        },
        onError: (error: unknown) => {
          toast({
            title: t($ => $.notifications.error.title),
            description: getErrorMessage(error),
            variant: 'error',
          });
        },
      },
    );
  };

  useImperativeHandle(
    ref,
    () => ({
      edit: handleOpen,
    }),
    [],
  );

  return (
    <Dialog
      onClose={handleClose}
      open={open}
      title={t($ => $.title)}
      size="large"
      primaryButton={{
        form: 'roles-form',
        label: allT($ => $.save),
        loading: editRoleMutation.isPending,
        type: 'submit',
      }}
      secondaryButton={{
        disabled: editRoleMutation.isPending,
        label: allT($ => $.cancel),
        onClick: handleClose,
      }}
    >
      <Form
        onSubmit={handleSubmit}
        defaultValues={{
          decryptAccess: getDecryptAccess(role.scopes),
          vaultProxyConfigs: vaultProxyOptions,
          name: role.name,
          access: getAccess(role.scopes),
          kind: formKind,
          entityTags: getRoleEntityTags(role.scopes).join(', '),
        }}
        kind={formKind}
      />
    </Dialog>
  );
});

export type { EditDialogProps, EditHandler };
export default EditDialog;
